// IndexedDB cache of synced rows, so a reload paints instantly from local data and then only
// fetches what changed. Everything here is best-effort: if IndexedDB fails we just re-download.
type Row = { id: string } & Record<string, unknown>;
type Meta = { version: number; schema: string };

const DB_NAME = "hhl-ops-sync";
let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore("rows");
      req.result.createObjectStore("meta");
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

const done = (tx: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

export async function loadCache(): Promise<(Meta & { tables: Record<string, Row[]> }) | null> {
  const db = await open();
  const tx = db.transaction(["rows", "meta"], "readonly");
  const metaReq = tx.objectStore("meta").get("state");
  const rowsReq = tx.objectStore("rows").getAll();
  await done(tx);
  const meta = metaReq.result as Meta | undefined;
  if (!meta) return null;
  const tables: Record<string, Row[]> = {};
  for (const { t, row } of rowsReq.result as { t: string; row: Row }[]) (tables[t] ??= []).push(row);
  return { ...meta, tables };
}

export async function replaceCache(tables: Record<string, Row[]>, meta: Meta) {
  const db = await open();
  const tx = db.transaction(["rows", "meta"], "readwrite");
  const rows = tx.objectStore("rows");
  rows.clear();
  for (const [t, list] of Object.entries(tables)) for (const row of list) rows.put({ t, row }, `${t}/${row.id}`);
  tx.objectStore("meta").put(meta, "state");
  await done(tx);
}

// Live changes are batched into one write every 300 ms.
let queue: { t: string; id: string; row: Row | null }[] = [];
let queuedMeta: Meta | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

export function queueChanges(changes: { t: string; id: string; row: Row | null }[], meta: Meta) {
  queue.push(...changes);
  queuedMeta = meta;
  if (!timer) timer = setTimeout(flush, 300);
}

async function flush() {
  timer = null;
  const batch = queue;
  const meta = queuedMeta;
  queue = [];
  if (!meta) return;
  try {
    const db = await open();
    const tx = db.transaction(["rows", "meta"], "readwrite");
    const rows = tx.objectStore("rows");
    for (const c of batch) c.row ? rows.put({ t: c.t, row: c.row }, `${c.t}/${c.id}`) : rows.delete(`${c.t}/${c.id}`);
    tx.objectStore("meta").put(meta, "state");
    await done(tx);
  } catch {
    await clearCache().catch(() => {});
  }
}

export async function clearCache() {
  queue = [];
  const db = await open();
  const tx = db.transaction(["rows", "meta"], "readwrite");
  tx.objectStore("rows").clear();
  tx.objectStore("meta").clear();
  await done(tx);
}
